// Chat-session state. History is persisted per user in MongoDB (see
// api/history.ts); a new chat exists only locally (id "s_<timestamp>") until
// its first message is sent, at which point ChatPage creates it server-side
// and swaps in the real id via replaceSessionId. Only the active chat id and
// radius are kept in localStorage.

import { useCallback, useEffect, useState } from "react";

import { deleteRemoteSession, fetchSessions } from "../api/history";
import type { ChatSession, DisplayMessage } from "../types";

const LEGACY_STORAGE_KEY = "gridscout_chat_sessions";
const ACTIVE_ID_KEY = "gridscout_active_session_id";
const RADIUS_KEY = "gridscout_radius_km";

export const isLocalSessionId = (id: string) => id.startsWith("s_");

function loadActiveId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ID_KEY);
  } catch {
    return null;
  }
}

function loadRadius(): number {
  try {
    const raw = localStorage.getItem(RADIUS_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : 100;
  } catch {
    return 100;
  }
}

/**
 * Chat history is per-login, not per-browser: wipe it from storage on
 * logout so a different (or the same) user signing back in starts fresh
 * instead of seeing the previous session's chats.
 */
export function clearStoredChatSessions() {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_ID_KEY);
  } catch {
    // ignore
  }
}

export function titleFromMessage(text: string): string {
  const trimmed = text.trim();
  return trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed;
}

export function useChatSessions() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(loadActiveId);
  const [radiusKm, setRadiusKm] = useState<number>(loadRadius);

  useEffect(() => {
    let cancelled = false;

    fetchSessions()
      .then((remote) => {
        if (cancelled) return;

        // Keep any not-yet-persisted local chat created while loading.
        setSessions((prev) => [...remote, ...prev.filter((s) => isLocalSessionId(s.id))]);
        setActiveSessionId((current) =>
          current && (isLocalSessionId(current) || remote.some((s) => s.id === current)) ? current : null
        );
      })
      .catch(() => {
        // History unavailable: continue with an empty, local-only list.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      if (activeSessionId) localStorage.setItem(ACTIVE_ID_KEY, activeSessionId);
      else localStorage.removeItem(ACTIVE_ID_KEY);
    } catch {
      // ignore
    }
  }, [activeSessionId]);

  useEffect(() => {
    try {
      localStorage.setItem(RADIUS_KEY, String(radiusKm));
    } catch {
      // ignore
    }
  }, [radiusKm]);

  const createSession = useCallback((): string => {
    const id = `s_${Date.now()}`;
    const newSession: ChatSession = { id, title: "New chat", messages: [] };

    setSessions((prev) => [...prev, newSession]);
    setActiveSessionId(id);

    return id;
  }, []);

  const deleteSession = useCallback(
    (id: string) => {
      setSessions((prev) => prev.filter((s) => s.id !== id));

      if (!isLocalSessionId(id)) {
        deleteRemoteSession(id).catch(() => {
          // best effort; it will reappear on next load if this failed
        });
      }

      setActiveSessionId((current) => {
        if (current !== id) return current;
        const remaining = sessions.filter((s) => s.id !== id);
        return remaining.length > 0 ? remaining[remaining.length - 1].id : null;
      });
    },
    [sessions]
  );

  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;

  const updateSessionMessages = useCallback((id: string, updater: (messages: DisplayMessage[]) => DisplayMessage[]) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, messages: updater(s.messages) } : s)));
  }, []);

  const setSessionTitle = useCallback((id: string, title: string) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
  }, []);

  const replaceSessionId = useCallback((oldId: string, newId: string) => {
    setSessions((prev) => prev.map((s) => (s.id === oldId ? { ...s, id: newId } : s)));
    setActiveSessionId((current) => (current === oldId ? newId : current));
  }, []);

  const clearAllSessions = useCallback(() => {
    setSessions([]);
    setActiveSessionId(null);
  }, []);

  return {
    sessions,
    activeSessionId,
    activeSession,
    radiusKm,
    setRadiusKm,
    setActiveSessionId,
    createSession,
    deleteSession,
    updateSessionMessages,
    setSessionTitle,
    replaceSessionId,
    clearAllSessions,
  };
}
